import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BaseDialogComponent, RefModelStoreService} from '@q9elements/ui-kit/common';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {Observable} from 'rxjs';
import {RmeNodeDaoAbstractService} from '../../../core/abstract/rme-node-dao-abstract.service';
import {CreateNodeDialogData} from '../../models/custom-view.interface';

@Component({
  selector: 'app-create-node-dialog',
  templateUrl: './create-node-dialog.component.html',
  styleUrls: ['./create-node-dialog.component.scss']
})
export class CreateNodeDialogComponent extends BaseDialogComponent implements OnInit {
  private customIdPattern: RegExp = /^[a-zA-Z0-9_\-\s\[\](){}\.]+$/i;
  public addNodeForm: FormGroup;
  public loading: boolean;
  public refModelGdprEnabled: boolean;
  public nameRequired: boolean;
  public customIdRequired: boolean;
  private nameBaseValidators: any[] = [Q9Validators.minLength(2), Q9Validators.maxLength(250)];
  private customIdBaseValidators: any[] = [
    Q9Validators.pattern(this.customIdPattern),
    Q9Validators.maxLength(60)
  ];

  constructor(
    private nodeSvc: RmeNodeDaoAbstractService,
    private rmeStore: RefModelStoreService,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<CreateNodeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateNodeDialogData
  ) {
    super();
  }

  ngOnInit() {
    this.refModelGdprEnabled = this.rmeStore.refModel.gdprEnabled;
    this.initForm();
  }

  private initForm() {
    this.addNodeForm = new FormGroup({
      name: new FormControl('', [Q9Validators.required(), ...this.nameBaseValidators]),
      customId: new FormControl('', [Q9Validators.required(), ...this.customIdBaseValidators]),
      description: new FormControl('', Q9Validators.maxLength(5000)),
      gdprEnabled: new FormControl(false)
    });
    this.nameRequired = true;
    this.customIdRequired = true;

    this.addNodeForm.valueChanges.subscribe(() => this.resetValidation());
  }

  private resetValidation(): void {
    const nameControl = this.addNodeForm.get('name');
    const customIdControl = this.addNodeForm.get('customId');
    const name = nameControl.value ? nameControl.value.trim() : null;
    const customId = customIdControl.value ? customIdControl.value.trim() : null;

    this.nameRequired = !customId;
    const nameValidators = !customId
      ? [Q9Validators.required(), ...this.nameBaseValidators]
      : this.nameBaseValidators;
    nameControl.setValidators(nameValidators);

    this.customIdRequired = !name;
    const customIdValidators = !name
      ? [Q9Validators.required(), ...this.customIdBaseValidators]
      : this.customIdBaseValidators;
    customIdControl.setValidators(customIdValidators);

    this.cdr.detectChanges();
  }

  override onSubmit() {
    if (this.addNodeForm.invalid) {
      this.addNodeForm.markAllAsTouched();
      this.addNodeForm.markAsDirty();

      return;
    }

    this.sendSubmitRequest();
  }

  protected override sendSubmitRequest(): Observable<unknown> {
    this.loading = true;
    const parentId = this.data.refModelId === this.data.parentId ? null : this.data.parentId;
    const nodeInfo: any = Object.assign(
      {},
      {
        name: this.addNodeForm.value.name ? this.addNodeForm.value.name.trim() : '',
        customId: this.addNodeForm.value.customId ? this.addNodeForm.value.customId.trim() : '',
        description: this.addNodeForm.value.description
          ? this.addNodeForm.value.description.trim()
          : '',
        parentId
      }
    );
    if (this.addNodeForm.value.gdprEnabled) {
      nodeInfo.gdpr = {enabled: true};
    }

    this.nodeSvc.addNewNode(this.data.refModelId, nodeInfo).subscribe(
      newNodeData => {
        this.dialogRef.close(newNodeData);
      },
      error => {
        console.error('Failed to add new node ', error);
        this.dialogRef.close();
      }
    );

    return;
  }
}
